import {gql} from '@apollo/client';
import {BUILDER_BANK_ACCOUNT} from '../features/builder/builderPotFragments';
import {FEE_PROFILE_FRAGMENT} from './feeProfileFragment';

export {FEE_PROFILE_FRAGMENT};

export const ATR_FRAGMENT = gql`
  fragment AtrFragment on AtrType {
    id
    value
    amendedValue
    finalValue
    dateCreated
    dateUpdated
    dateSubmitted
    pending
    atrTool
    amendReason
    dateSent
  }
`;

export const REVIEW_VALIDATION_FRAGMENT = gql`
  fragment ReviewValidationFragment on ReviewValidationType {
    header
    goals
    tasks
    recs
    notes
    noChanges
    risk
    vulnerabilities
  }
`;

export const ADDRESS_FRAGMENT = gql`
  fragment AddressFragment on AddressType {
    id
    line1
    line2
    line3
    city
    postcode
    country
    county
    composite
  }
`;

export const CONTACT_BASIC_FRAGMENT = gql`
  fragment ContactBasicFragment on ContactType {
    id
    curoId
    fullName
    primaryPhone
    contactStatus
    homePhone
    dob
    isDeleted
    email
    name
    firstName
    spouseName
    simplifiedName
    isVulnerable
    isMinor
    accountContacts {
      account {
        id
      }
      isPrimary
      isSecondary
    }
    lastName
    title
    nationalInsurance
    nationality
    fullAddress
    addresses {
      postcode
      country
    }
    dataValidation {
      isValid
      errors {
        field
        message
      }
    }
  }
`;

export const ACCOUNT_BASIC_FRAGMENT = gql`
  ${CONTACT_BASIC_FRAGMENT}
  fragment AccountBasicFragment on AccountType {
    id
    curoId
    name
    isTrust
    hmrcReference
    leiNumber
    businessUnit {
      id
      divisionName
      partnershipType
      name
    }
    ongoingFee
    initialFixedFee
    initialPercentageFee
    accountStatus
    propositionType
    secondaryContact {
      id
      name
      simplifiedName
    }
    primaryContact {
      id
      name
      simplifiedName
    }
    contacts {
      ...ContactBasicFragment
    }
    accountType
    dataValidation {
      isValid
      errors {
        field
        message
      }
    }
  }
`;

export const VULNERABILITY_FRAGMENT = gql`
  fragment Vulnerabilities on ContactType {
    ageVulnerability
    otherVulnerability
    healthVulnerability
    hearingVulnerability
    languageVulnerability
    learningVulnerability
    visionVulnerability
    mentalHealthVulnerability
    mentalCapacityVulnerability
    genderReassignment
    divorceVulnerability
    bereavementVulnerability
    inexperienceVulnerability
    financialDifficultiesVulnerability
    otherEventVulnerability
    specialCareReasons
  }
`;

export const ACCOUNT_VULNERABILITIES_FRAGMENT = gql`
  ${VULNERABILITY_FRAGMENT}
  fragment AccountVulnerabilitiesFragment on AccountType {
    id
    primaryContact {
      id
      simplifiedName
      ...Vulnerabilities
      giaHasLiquidityNeeds
      pensionHasLiquidityNeeds
    }
    secondaryContact {
      id
      simplifiedName
      ...Vulnerabilities
      giaHasLiquidityNeeds
      pensionHasLiquidityNeeds
    }
    giaHasLiquidityNeeds
    pensionHasLiquidityNeeds
    isTrust
  }
`;

export const ACCOUNT_SEARCH_FRAGMENT = gql`
  fragment AccountSearchFragment on AccountType {
    id
    curoId
    name
    plans
    city
    adviser {
      id
      name
    }
    businessUnit {
      id
      divisionName
      partnershipType
      name
    }
    primaryPhone
    clientType
    propositionType
    accountStatus
    secondaryContact {
      id
      name
    }
    primaryContact {
      id
      name
      email
    }
  }
`;

export const CONTACT_SEARCH_FRAGMENT = gql`
  fragment ContactSearchFragment on ContactType {
    id
    curoId
    fullName
    accountContacts {
      id
      isPrimary
      account {
        id
        clientType
        propositionType
        accountType
      }
    }
    businessUnit {
      id
      divisionName
      partnershipType
    }
    primaryPhone
    contactStatus
    homePhone
    dob
    isDeleted
    email
    name
    firstName
    spouseName
    simplifiedName
    # otherVulnerability
    # otherEventVulnerability
    # specialCareReasons
    isVulnerable
    isMinor
    # understandsPrivateAssets
    # giaHasLiquidityNeeds
    # pensionHasLiquidityNeeds
  }
`;

export const CONTACT_RAG_STATUSES_FRAGMENT = gql`
  fragment ContactRAGStatusesFragment on ContactType {
    id
    name
    simplifiedName
    primaryPhone
    homePhone
    workPhone
    secondaryPhone
    email
    isEmailUnique
    contactStatus
    isVulnerable
    curoId
    atrStatus
    ecqStatus
    statuses {
      atr {
        dateSent
        dateSubmitted
        status
        lastUpdated
      }
      factFind {
        status
        lastSeen
        isSubmitted
        pagesCompleted
        dateSubmitted
        dateSent
      }
      clientPortal {
        status
        lastUpdated
      }
    }
  }
`;

export const ACCOUNT_CONTACT_FRAGMENT = gql`
  ${CONTACT_SEARCH_FRAGMENT}
  ${CONTACT_BASIC_FRAGMENT}
  ${CONTACT_RAG_STATUSES_FRAGMENT}
  fragment AccountContact on ContactType {
    ...ContactSearchFragment
    ...ContactBasicFragment
    ...ContactRAGStatusesFragment
  }
`;

// contains only the slow loading parts of account contact
export const ACCOUNT_CONTACT_SLOW = gql`
  ${ACCOUNT_CONTACT_FRAGMENT}
  fragment AccountContactSlow on ContactType {
    ...AccountContact
    fpa {
      id
      status
      dateLastSent
      dateSigned
    }
  }
`;

export const STAFF = gql`
  fragment Staff on StaffType {
    id
    firstName
    lastName
    email
    businessUnit {
      id
      name
    }
    isPad
    name
  }
`;

export const STAFF_DETAILS = gql`
  fragment StaffDetails on StaffType {
    id
    name
    email
    isPad
    curoId
    groups {
      id
      name
    }
    businessUnit {
      id
      name
      partnershipType
    }
    permissionOverride
    userPerms {
      id
      codename
    }
    groupPerms {
      id
      codename
    }
  }
`;

export const FEE_FRAGMENT = gql`
  fragment FeeFragment on FeeComponentType {
    id
    feeType
    feeBasis
    name
    curoId
    feeName {
      id
      name
    }
    taxRate {
      id
      name
    }
    chargeFor
    chargeReason
    frequency
    percentageAgreed
    annualPercentage
    amount
    calculationBasis
    collectionMethod
    status
    statusReason
    tiers
  }
`;

export const REVIEW_SUMMARY_FRAGMENT = gql`
  fragment ReviewSummaryFragment on ReviewType {
    id
    account {
      id
      name
    }
    reviewStatus
    name
    dateScheduled
    dateBooked
  }
`;

export const AVAILABLE_FEES_FRAGMENT = gql`
  ${FEE_FRAGMENT}
  fragment AvailableFeeFragment on AvailableFeesType {
    fees {
      ...FeeFragment
    }
    required
  }
`;

export const REVIEW_SUMMARY = gql`
  fragment ReviewSummary on ReviewType {
    id
    curoId
    name
    statusOneview
    validation {
      ...ReviewValidationFragment
    }
    validationContacts {
      id
      simplifiedName
      dataValidation {
        isValid
        errors {
          field
          message
        }
      }
    }
  }
  ${REVIEW_VALIDATION_FRAGMENT}
`;

export const REVIEW = gql`
  fragment Review on ReviewType {
    id
    curoId
    # account {
    #   id
    #   curoId
    # }
    name
    reviewReason
    status
    dateBooked
    dateScheduled
    isDraft
    statusOneview
    isInJourney
    isFailedSubmission
    executionOnly
    isSoftSubmitted
    adviser {
      id
      name
    }
    owner {
      id
      name
    }
    invitation {
      invitationOwner {
        name
      }
    }
    date
    dateUpdated
    dateCreated
    stage
    precursorNotFinished
    postcursorNotFinished
    reviewAction
    # validation {
    #   ...ReviewValidationFragment
    # }
  }
`;

export const GOAL = gql`
  fragment Goal on GoalType {
    id
    curoId
    isInCuro
    name
    title
    description
    targetIncome
    targetCapital
    position
    priority
    isFailedSubmission
    goalType
    statusAtReview
    ownership
    targetAge
    dateCreated
    targetYears
    targetEndDate
    drawdownRate
    planningObjective
    planningObjectivePurpose
    objectiveType
    contact {
      id
      simplifiedName
    }
    secondaryContact {
      id
      simplifiedName
    }
    review {
      id
    }
  }
`;

export const POT = gql`
  fragment PotDetails on PotType {
    id
    curoId
    name
    isDeleted
    full
    isCash
    productIsBank
    bankDetails
    currentDirectValue
    valuationMethod
    proposalReference
    feeProfiles {
      ...FeeProfileFragment
    }
    productName {
      id
      formattedName
      acronym
      reportName
    }
    isValidated
    policyHolder
    originalOvrId
    isDataGather
    isAuthorityHeld
    productProvider {
      id
      curoId
      name
      providerType
      isFavourite
      isPanel
      formattedName
      isBank
      reportName
      isSaltus
      showMandateSelector
    }
    totalValue
    isSaltus
    review {
      id
      isSpp
      isSppDa
      isSppAr
      isSppRi
    }
    usedValue
    parent {
      id
    }
    planNumber
    planStatus
    withdrawalReason
    sortCode
    isAuthorised
    nominatedAccount {
      id
      name
    }
    contact {
      id
      simplifiedName
    }
    # ownership
    isDummy
    modelPortfolio {
      id
      name
      providerContact {
        id
        name
        lastName
        jobTitle
      }
    }
    servicingRights
    inSpecie
    isShellPlan
    mandate
    inTrust
    crystallisedFunds
    uncrystallisedFunds
    # protectionType
    # livesAssured
    # premiumType
    # lifeCover
    # terminatingAge
    # criticalIllnessCover
    # lifeOrCriticalIllnessCover
    # termType
    # term
    # indexation
    # benefitBasis
    # benefitBasisMonthlyText
    # deferredPeriod
    notes
  }
  fragment Pot on PotType {
    ...PotDetails
    children {
      ...PotDetails
    }
  }
  ${FEE_PROFILE_FRAGMENT}
`;

export const ACT_GROUP = gql`
  fragment ActivityGroup on ActivityGroupType {
    id
    name
    formattedName
    t4aMaintained
    activitySubGroups {
      id
      name
      description
      subject
      requiredEntity
    }
  }
`;

export const MODEL_PORTFOLIO_FRAGMENT = gql`
  fragment ModelPortfolioFragment on ModelPortfolioType {
    id
    name
    providerContact {
      id
      name
      lastName
      jobTitle
    }
    riskProfile
  }
`;

export const OVRPOT_POT_FRAGMENT = gql`
  ${POT}
  fragment OVRPot_Pot on PotType {
    contact {
      id
      name
    }
    account {
      id
      name
    }
    ...Pot
  }
`;

export const OV_POT_FRAGMENT = gql`
  ${OVRPOT_POT_FRAGMENT}
  ${FEE_FRAGMENT}
  ${MODEL_PORTFOLIO_FRAGMENT}
  ${BUILDER_BANK_ACCOUNT}
  fragment OVPot on OVRecommendPotType {
    id
    full
    planType
    recurringClientAmount
    recurringClientFrequency
    singleClientAmount
    singleEmployerAmount
    recurringEmployerAmount
    recurringEmployerFrequency
    hasFeeDeviation
    sppOngoingFpFeePercentage
    fees {
      ...FeeFragment
    }
    initialFee {
      id
    }
    initialEmployerFee {
      id
    }
    legacyInitialFee
    legacyInitialEmployerFee
    inSpecie
    mandate
    modelPortfolio {
      ...ModelPortfolioFragment
    }
    pot {
      ...OVRPot_Pot
    }
    # DBO-1436 union of pot and bank account. Only the bank-account member is
    # selected here: pot-backed rows already expose their pot via "pot" above,
    # while withdrawal bank-account destination rows have pot = null.
    potBankUnion {
      ... on BankAccountType {
        ...BuilderBankAccount
      }
    }
    adhocFeeType
    adhocFeeAmount
    adhocFeeDate
    adhocFeeReason
    recommendations {
      id
    }
    annuityType
    incomeFrequency
    guaranteedPeriod
    spouseAnnuityPercent
    rateType
    indexLinked
    ltcCapitalProtected
    protectedPercentage
    paymentType
    notes
  }
`;

export const RECOMMENDS_FRAGMENT = gql`
  fragment RecommendsFragment on RecommendationType {
    id
    name
    curoId
    contribution
    contributionFrequency
    employeeContribution
    employerContribution
    withdrawalAmount
    totalContributionPremium
    isFailedSubmission
    advice
    adviceAction
    recommendationStatus
    projects {
      id
      name
      category {
        id
        name
      }
      stage {
        id
        name
        mappedName
        projectCategory {
          id
          name
          mappedName
        }
        colour
      }
    }
  }
`;

export const OV_RECOMMENDATION = gql`
  ${OV_POT_FRAGMENT}
  ${RECOMMENDS_FRAGMENT}
  fragment OVRecommendation on OVRecommendType {
    id
    name
    dateUpdated
    dateCreated
    solutionType
    isXo
    isSppDaXo
    isPensionSharing
    isDependantsPension
    isAps
    progressTrackerStatus
    goal {
      id
      name
    }
    selectedMandate
    review {
      id
      dateSubmitted
    }
    contact {
      id
      simplifiedName
    }
    account {
      id
    }
    productProvider {
      id
      name
      isBank
      reportName
      isSaltus
    }
    productName {
      id
      name
      reportName
    }
    recommends {
      ...RecommendsFragment
    }
    pots {
      ...OVPot
    }
    urgency
    urgencyReason
    isFinalised
    action
    isNewPlanRequired
    # ownership
    planType
    withdrawalReason
    withdrawalAmount
    startDate
    endDate
    isDentist
    note
    parentGuardian {
      id
    }
  }
`;

export const ATTITUDE_RISK = gql`
  fragment AttitudeRisk on AtrType {
    id
    curoId
    name
    state
    account {
      id
    }
    contact {
      id
    }
    value
  }
`;

export const TASK = gql`
  fragment Task on TaskType {
    id
    subject
    urgency
    curoId
    dueDate
    startDate
    isWaiting
    isActivityReport
    description
    priority
    statusReason
    percentComplete
    actualStart
    activityStatus
    regardingType
    isFailedSubmission
    selectedEntityType
    selectedEntityId
    regardingContact {
      id
    }
    regardingPot {
      id
    }
    regardingGoal {
      id
    }
    regardingThirdParty {
      id
      name
    }
    regardingReview {
      id
    }
    regarding
    activityGroup {
      id
      name
      formattedName
    }
    activitySubGroup {
      id
      name
    }
    review {
      id
    }
    contact {
      id
      simplifiedName
    }
    adviser {
      id
      name
    }
    account {
      id
    }
    owner {
      id
      name
    }
  }
`;

export const NOTE_FRAGMENT = gql`
  fragment NoteFragment on NoteType {
    id
    name
    displayName
    body
    reviewed
    dateUpdated
    dateCreated
    required
    isVisible
    markupBody
  }
`;

export const INVITATION_DETAILS_FRAGMENT = gql`
  fragment InvitationDetailsFragment on InvitationType {
    id
    isDeleted
    isDummy
    dateDeleted
    dateCreated
    dateUpdated
    createdBySystem
    review {
      id
    }
    account {
      id
      name
    }
    sentByStaff {
      id
      name
    }
    firstContact {
      id
      name
      email
      forename: firstName
      surname: lastName
    }
    secondContact {
      id
      name
      email
      forename: firstName
      surname: lastName
    }
    firstContactNotificationId
    secondContactNotificationId
    bookingLink
    zoomLink
    rescheduleUrl
    cancelUrl
    phoneNumber
    meetingType
    status
    dateSent
    dateReminderSent
    dateConfirmed
    dateBooked
    clientCommunicationAttempts {
      contactDate
      contactMethod
      declineReason
    }
    propositionType
    owner {
      id
      name
    }
    invitationOwner {
      id
      name
    }
    businessUnit {
      id
      name
    }
    phoneNumberMatchesContactsMessage
  }
`;

export const INVITATION_SUMMARY_FRAGMENT = gql`
  fragment InvitationSummaryFragment on InvitationType {
    id
    dateUpdated
    sentByStaff {
      id
      name
    }
    meetingType
    dateSent
    dateBooked
    invitationOwner {
      id
      name
    }
  }
`;

export const REVIEW_DETAILS_FRAGMENT = gql`
  fragment ReviewDetailsFragment on ReviewType {
    id
    curoId
    isFailedSubmission
    dateSubmitted
    adviceAction
    isSpp
    owner {
      id
      name
    }
    account {
      id
      name
      investorCategory
      giaHasLiquidityNeeds
      pensionHasLiquidityNeeds
    }
    contacts {
      id
      curoId
      name
      simplifiedName
    }
    validationContacts {
      id
      simplifiedName
      dataValidation {
        isValid
        errors {
          field
          message
        }
      }
    }
    contact {
      id
      name
    }
    adviser {
      id
      name
    }
    padReviewer {
      id
      name
    }
    notesEdited
    isDraft
    isSoftSubmitted
    status
    stage
    dateBooked
    isInJourney
    dateScheduled
    annualReview
    name
    focus
    reviewReason
    reviewFrequency
    reviewType
    atrRequested
    reasonNoAtr
    meetingType
    clientType
    propositionType
    conversionClient
    precursorNotFinished
    postcursorNotFinished
    opportunityValue
    performanceReviewProvided
    performanceReviewDate
    compliantCase
    taxYearEnd
    dateUpdated
    noAttendeeReason
    executionOnly
    reviewAction
    statusOneview
    sendForSignoff
    clientCommunicationAttempts {
      contactDate
      contactMethod
      declineReason
    }
    validation {
      ...ReviewValidationFragment
    }
  }
  ${REVIEW_VALIDATION_FRAGMENT}
`;

export const REVIEW_INVITATION_DETAILS_FRAGMENT = gql`
  fragment ReviewInvitationDetailsFragment on ReviewType {
    ...ReviewDetailsFragment
    atrStatus
    ecqStatus
    allowedActions
    invitation {
      ...InvitationDetailsFragment
    }
    contacts {
      id
      simplifiedName
      isVulnerable
      curoId
      name
      email
      isEmailUnique
      contactStatus
    }
  }
  ${INVITATION_DETAILS_FRAGMENT}
  ${REVIEW_DETAILS_FRAGMENT}
`;

export const REVIEW_INVITATION_DETAILS_FRAGMENT_SLOW = gql`
  fragment ReviewInvitationDetailsSlowFragment on ReviewType {
    id
    contacts {
      ...ContactRAGStatusesFragment
    }
  }
  ${CONTACT_RAG_STATUSES_FRAGMENT}
`;

export const REVIEW_INVITATION_SUMMARY_FRAGMENT = gql`
  fragment ReviewInvitationSummaryFragment on ReviewType {
    id
    dateSubmitted
    dateScheduled
    noAttendeeReason
    propositionType
    account {
      id
      name
    }
    owner {
      id
      name
    }
    adviser {
      id
      name
    }
    contacts {
      id
      name
      email
      isEmailUnique
      contactStatus
      simplifiedName
    }
    atrStatus
    ecqStatus
    invitation {
      ...InvitationSummaryFragment
    }
  }
  ${INVITATION_SUMMARY_FRAGMENT}
`;

export const REVIEW_SUBMISSION_FRAGMENT = gql`
  fragment ReviewSubmissionFragment on ReviewType {
    id
    isFailedSubmission
    isDeleted
    status
    curoId
    adviceAction
    account {
      id
    }
    reviewAction
    stage
    isDraft
    precursorNotFinished
    postcursorNotFinished
    goals {
      id
      isFailedSubmission
      isDeleted
    }
    tasks {
      id
      isFailedSubmission
      isDeleted
      curoId
    }
    recommendations {
      id
      isFailedSubmission
      isDeleted
      curoId
    }
    ovrecommends {
      id
      isFinalised
    }
  }
`;

export const NOTE_DETAILS_FRAGMENT = gql`
  ${NOTE_FRAGMENT}
  fragment HandoverNotesReviewDetailsFragment on ReviewType {
    id
    curoId
    name
    date
    meetingType
    propositionType
    focus
    requireRiskProfile
    reviewReason
    dateCreated
    dateScheduled
    dateBooked
    contact {
      name
      id
    }
    notes {
      ...NoteFragment
    }
    validation {
      ...ReviewValidationFragment
    }
    validationContacts {
      id
      simplifiedName
      dataValidation {
        isValid
        errors {
          field
          message
        }
      }
    }
  }
  ${REVIEW_VALIDATION_FRAGMENT}
`;

export const LEAD_DETAILS_FRAGMENT = gql`
  ${CONTACT_SEARCH_FRAGMENT}
  fragment LeadDetailsFragment on LeadType {
    id
    isDeleted
    isDummy
    dateDeleted
    dateCreated
    dateUpdated
    account {
      id
      getLatestReview {
        id
      }
    }
    contact {
      ...ContactSearchFragment
    }
    # staffReferrer
    # adviser
    # owner
    # relatedLead
    # currency
    curoId
    accountType
    title
    firstName
    lastName
    accountName
    primaryEmail
    secondaryEmail
    mobilePhone
    homePhone
    jobTitle
    companyName
    linkedinProfile
    dob
    nationalInsurance
    leadSource
    leadQualityCode
    doNotSendMm
    budgetAmount
    ongoingFee
    initialFixedFee
    initialPercentageFee
    subject
    initialMeeting
    lastEventDate
    doNotBulkEmail
    leadType
    requirementType
    statusCode
    isSubmittedFromOneview
    # primaryLeads
    # tasks
    # phonecalls
    addresses {
      ...AddressFragment
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export const REVIEW_PAGE_FRAGMENT = gql`
  fragment ReviewPageFragment on StaffType {
    id
    name
    activeReviews {
      id
      account {
        id
        name
      }
      reviewStatus
      name
      dateScheduled
      dateBooked
    }
  }
`;

export const HISTORY_DIFF_FRAGMENT = gql`
  fragment HistoryDiffDetails on HistoryDiffType {
    date
    user
    field
    oldValue
    newValue
    reason
    record
    extra
  }
`;
export const ACCOUNT_DETAILS_FRAGMENT = gql`
  ${CONTACT_BASIC_FRAGMENT}
  fragment AccountDetailsFragment on AccountType {
    id
    curoId
    name
    primaryPhone
    email
    income
    netAssets
    propertyAssets
    totalAssets
    accountStatus
    inHousePlans
    initialFixedFee
    initialPercentageFee
    ongoingFee
    propositionType
    hmrcReference
    leiNumber
    businessUnit {
      id
      name
      divisionName
      partnershipType
    }
    city
    otherPlans
    owner {
      id
      name
    }
    plans
    liabilities
    dateCreated
    dateBecameClient
    accountType
    isFailedSubmission
    investorCategory
    clientType
    propositionType
    adviser {
      id
      name
    }
    primaryContact {
      ...ContactBasicFragment
    }
    secondaryContact {
      ...ContactBasicFragment
    }
    giaHasLiquidityNeeds
    pensionHasLiquidityNeeds
    ...AccountBasicFragment
  }
  ${ACCOUNT_BASIC_FRAGMENT}
`;

export const NOTIFICATION_FRAGMENT = gql`
  fragment NotificationFragment on NotificationType {
    id
    dateCreated
    dateUpdated
    expiresAfter
    message
  }
`;

export const JUNIOR_ADVISER_DETAILS = gql`
  fragment JuniorAdviserDetails on StaffType {
    id
    name
  }
`;

export const ADVISER_DETAILS = gql`
  fragment AdviserDetails on StaffType {
    id
    name
    email
    isAdviser
  }
`;

export const RiskSectionFragment = gql`
  fragment RiskSectionFragment on RiskProfileSectionType {
    riskType
    notes
    score
    noChange
    createdAt
    updatedAt
  }
`;
export const RiskProfileInitialScoreFragment = gql`
  fragment RiskProfileInitialScoreFragment on RiskProfileInitialScoreType {
    score
    assessedAt
    expiryDate
    isExpired
    id
  }
`;

export const RiskProfileFragment = gql`
  fragment RiskProfileFragment on RiskProfileType {
    contact {
      id
      curoId
    }
    outcomeRiskScore
    initialRiskProfile {
      ...RiskProfileInitialScoreFragment
    }
    initialRiskToleranceQuestionnaire {
      ...RiskProfileInitialScoreFragment
    }
    noChangeReasonNotes
    dfmAgreed
    hasNoRiskDicussion
    isRiskToleranceQuestionnaireDeclined
    riskToleranceQuestionnaireDeclineReason
    centralPropositionAgreed
    outcomeRiskNotes
    updatedAt
    createdAt
    sections {
      ...RiskSectionFragment
    }
    previousSections {
      ...RiskSectionFragment
    }
  }
  ${RiskSectionFragment}
  ${RiskProfileInitialScoreFragment}
`;

export const INVITATION_EMAIL_AUDIT_FRAGMENT = gql`
  fragment InvitationEmailAuditFragment on InvitationEmailAuditItemType {
    status
    dateSent
    contact {
      id
      name
      email
    }
  }
`;
export const COGNITO_USER_FRAGMENT = gql`
  fragment CognitoUserFragment on CognitoUserType {
    id
    subId
    email
    enabled
    status
    groups
    emailVerified
    mfaEnabled
    mfaSetting
    # attributes
    createdAt
    lastModifiedAt
    username
  }
`;
